import React, { useState, useEffect } from 'react';
import { FileText, DollarSign, TrendingUp, CreditCard, Printer } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
    getPetInvoices,
    getPetBillingSummary,
    updateInvoiceStatus,
    updateOwnerCredit,
    Invoice,
    InvoiceStatus,
    PetBillingSummary,
    formatCurrency,
    getStatusColor,
    getStatusLabel
} from '../../utils/petBillingApi';

interface PetBillingProps {
    petId: string;
}

const PetBilling: React.FC<PetBillingProps> = ({ petId }) => {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [summary, setSummary] = useState<PetBillingSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [editingCredit, setEditingCredit] = useState(false);
    const [creditValue, setCreditValue] = useState<string>('0');
    const [savingCredit, setSavingCredit] = useState(false);

    const canEditCredit = user?.role === 'ADMIN' || user?.role === 'PRACTICE_ADMIN';

    useEffect(() => {
        fetchData();
    }, [petId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [invoicesData, summaryData] = await Promise.all([
                getPetInvoices(petId),
                getPetBillingSummary(petId)
            ]);
            setInvoices(invoicesData);
            setSummary(summaryData);
            setCreditValue(summaryData.credit_balance.toFixed(2));
        } catch (error) {
            console.error('Failed to fetch billing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (invoiceId: string, newStatus: InvoiceStatus) => {
        try {
            await updateInvoiceStatus(invoiceId, newStatus);
            await fetchData();
        } catch (error) {
            console.error('Failed to update status:', error);
            alert('Failed to update invoice status');
        }
    };

    const handleCreditSave = async () => {
        try {
            setSavingCredit(true);
            const amount = parseFloat(creditValue) || 0;
            await updateOwnerCredit(petId, amount);
            setEditingCredit(false);
            await fetchData();
        } catch (error) {
            console.error('Failed to update credit:', error);
            alert('Failed to update credit balance');
        } finally {
            setSavingCredit(false);
        }
    };

    const handlePrintInvoice = (invoice: Invoice) => {
        // Simple print implementation - opens invoice details in new window
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(`
                <html>
                <head>
                    <title>Invoice ${invoice.invoice_number}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                        h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
                        .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
                        .details { margin: 20px 0; }
                        .details p { margin: 5px 0; }
                        .amounts { background: #f7fafc; padding: 20px; border-radius: 8px; margin-top: 30px; }
                        .total { font-size: 24px; font-weight: bold; color: #1a365d; }
                        .status { display: inline-block; padding: 4px 12px; border-radius: 9999px; font-size: 14px; font-weight: 600; }
                        .status-open { background: #dbeafe; color: #1e40af; }
                        .status-due { background: #fef3c7; color: #92400e; }
                        .status-paid { background: #d1fae5; color: #065f46; }
                        .status-collections { background: #fee2e2; color: #991b1b; }
                        .status-uncollectible { background: #f3f4f6; color: #374151; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Invoice</h1>
                        <div>
                            <p><strong>Invoice #:</strong> ${invoice.invoice_number}</p>
                            <p><strong>Date:</strong> ${new Date(invoice.encounter_date).toLocaleDateString()}</p>
                            <p><span class="status status-${invoice.status}">${getStatusLabel(invoice.status)}</span></p>
                        </div>
                    </div>
                    <div class="amounts">
                        <p><strong>Subtotal:</strong> ${formatCurrency(invoice.subtotal)}</p>
                        <p><strong>Discount:</strong> -${formatCurrency(invoice.discount_total)}</p>
                        <p><strong>Tax:</strong> ${formatCurrency(invoice.tax_amount)}</p>
                        <hr style="margin: 10px 0; border: none; border-top: 1px solid #e2e8f0;">
                        <p class="total">Total: ${formatCurrency(invoice.total_amount)}</p>
                        <p><strong>Amount Paid:</strong> ${formatCurrency(invoice.amount_paid)}</p>
                        <p><strong>Balance Due:</strong> ${formatCurrency(invoice.balance_due)}</p>
                    </div>
                    ${invoice.notes ? `<div class="details"><p><strong>Notes:</strong> ${invoice.notes}</p></div>` : ''}
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading billing...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Current Balance */}
                <div className="bg-white rounded-none shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                        <DollarSign className="w-4 h-4" />
                        Current Balance
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                        {summary ? formatCurrency(summary.current_balance) : '$0.00'}
                    </div>
                </div>

                {/* Credit */}
                <div className="bg-white rounded-none shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                        <CreditCard className="w-4 h-4" />
                        Credit
                    </div>
                    {editingCredit ? (
                        <div className="flex items-center gap-2">
                            <span className="text-lg">$</span>
                            <input
                                type="number"
                                value={creditValue}
                                onChange={(e) => setCreditValue(e.target.value)}
                                className="w-24 text-xl font-bold border border-gray-300 rounded px-2 py-1"
                                min="0"
                                step="0.01"
                            />
                            <button
                                onClick={handleCreditSave}
                                disabled={savingCredit}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                {savingCredit ? '...' : 'Save'}
                            </button>
                            <button
                                onClick={() => {
                                    setEditingCredit(false);
                                    setCreditValue(summary?.credit_balance.toFixed(2) || '0');
                                }}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-green-600">
                                {summary ? formatCurrency(summary.credit_balance) : '$0.00'}
                            </span>
                            {canEditCredit && (
                                <button
                                    onClick={() => setEditingCredit(true)}
                                    className="text-xs text-blue-600 hover:text-blue-700 underline"
                                >
                                    Edit
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Lifetime Value */}
                <div className="bg-white rounded-none shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        Lifetime Value
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                        {summary ? formatCurrency(summary.lifetime_value) : '$0.00'}
                    </div>
                </div>

                {/* Trailing Year */}
                <div className="bg-white rounded-none shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        Trailing Year
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                        {summary ? formatCurrency(summary.trailing_year_value) : '$0.00'}
                    </div>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-white rounded-none shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Invoices</h2>

                {invoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No invoices yet.</p>
                        <p className="text-sm">Invoices are created when items are added to encounters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                                        Invoice #
                                    </th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                                        Total
                                    </th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                                        Balance
                                    </th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                                        Encounter Date
                                    </th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                                        Print
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id} className="bg-white hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                                            {invoice.invoice_number}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                            {formatCurrency(invoice.total_amount)}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium">
                                            <span className={invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}>
                                                {formatCurrency(invoice.balance_due)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={invoice.status}
                                                onChange={(e) => handleStatusChange(invoice.id, e.target.value as InvoiceStatus)}
                                                className={`px-2 py-1 text-xs font-semibold rounded-full border-0 cursor-pointer ${getStatusColor(invoice.status)}`}
                                            >
                                                <option value="open">Open</option>
                                                <option value="due">Due</option>
                                                <option value="paid">Paid</option>
                                                <option value="collections">Collections</option>
                                                <option value="uncollectible">Uncollectible</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            <a
                                                href={`/pet/${petId}/encounter/${invoice.encounter_id}/intake`}
                                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-none hover:bg-blue-100 transition-colors"
                                            >
                                                {new Date(invoice.encounter_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-')}
                                            </a>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => handlePrintInvoice(invoice)}
                                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                                title="Print Invoice"
                                            >
                                                <Printer className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PetBilling;

